import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { trackHeapEvent } from 'utils/analytics';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import NeedHelp from 'components/NeedHelp';
import { CALENDLY_LINK } from 'constants/common';
import heroImg from './imgs/billing-hero.png';
import benefitCoinsImg from './imgs/benefit-coins.png';
import benefitDocImg from './imgs/benefit-doc.png';
import s from './s.module.less';

type TBillingTab = 'benefits' | 'how' | 'pricing' | 'faqs' | 'feedback';

const TABS: { key: TBillingTab; label: string }[] = [
    { key: 'benefits', label: 'Benefits' },
    { key: 'how', label: 'How it works' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'faqs', label: 'FAQs' },
    { key: 'feedback', label: 'Share feedback' },
];

const HOW_STEPS = [
    {
        step: 'Step 1',
        title: 'Set up your billing profile',
        desc: 'Add your billing info, connect your EHR, and set up a payment method. The one-time setup takes less than 8 minutes, then Klarity can submit claims for you.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
            </svg>
        ),
    },
    {
        step: 'Step 2',
        title: 'Klarity files the claim within 24 hours',
        desc: 'After each session, Klarity drafts the claim with the right codes and submits it directly to the payer. If a claim gets denied, Klarity diagnoses the cause and files the appeal for you.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2 11 13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
        ),
    },
    {
        step: 'Step 3',
        title: 'You get paid, pay only 3.9%',
        desc: "Reimbursements land in your account, minus a 3.9% fee per successful claim. If a claim doesn't get paid, you pay nothing.",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v12M8 10h6a2 2 0 0 1 0 4H9a2 2 0 0 0 0 4h7" />
            </svg>
        ),
    },
];

const FAQS = [
    {
        q: 'What does "Managed Billing" include?',
        a: "Managed Billing is an optional service at 3.9% of successful reimbursements (you only pay when you get paid). It includes same-day claim submission, denial management & auto-appeals, insurance eligibility verification, and on-demand & weekly billing reports. There are no separate fees: everything is bundled into the 3.9% rate.",
    },
    {
        q: 'How is Klarity different from a virtual assistant or billing agency?',
        a: 'Klarity operates 24/7 inside the EHR with no turnover, no sick days, and no ramp-up time. Managed Billing charges 3.9% of successful reimbursements, roughly half what traditional billing agencies charge (5 to 10%). A typical solo practice saves $4K+ per month compared to hiring a VA plus a billing agency, with a 94% clean claim rate versus the 70 to 80% industry average.',
    },
    {
        q: 'Can we cancel anytime?',
        a: 'Yes. No contract required. Data is exported within 24 hours of cancellation in CSV/JSON format at no charge.',
    },
    {
        q: 'Is Klarity HIPAA compliant?',
        a: 'Yes. Klarity is fully HIPAA compliant with a standard Business Associate Agreement (BAA) in place. All patient data is encrypted end-to-end using AES-256 (in transit and at rest). No PHI is ever used for AI model training. Klarity maintains full audit logs and provides 24-hour preliminary breach notification with a 60-day full report.',
    },
    {
        q: 'How is provider data handled within your AI-assisted processes, and what safeguards are in place for sensitive information?',
        a: 'Klarity is fully HIPAA compliant with a standard Business Associate Agreement (BAA) in place. All patient data is encrypted and handled according to federal privacy standards.',
    },
];

type Props = {
    isTab?: boolean;
};

export default function InsuranceBillingLandingPage({ isTab = false }: Props) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TBillingTab>('benefits');
    const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});
    const [showWebinars, setShowWebinars] = useState(false);
    const sectionRefs = useRef<Partial<Record<TBillingTab, HTMLDivElement | null>>>({});
    const scrollLockRef = useRef(false);

    const handleStart = () => {
        trackHeapEvent('WebApp - Insurance Billing Landing - Click - Get Started', {
            tab: activeTab,
        });
        navigate(PATH.INSURANCE_BILLING_ONBOARDING);
    };

    const handleTabChange = (tab: TBillingTab) => {
        setActiveTab(tab);
        trackHeapEvent('WebApp - Insurance Billing Landing - Click - Tab', { tab });
        scrollLockRef.current = true;
        sectionRefs.current[tab]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
            scrollLockRef.current = false;
        }, 700);
    };

    const handleOpenWebinars = () => {
        trackHeapEvent('WebApp - Insurance Billing Landing - Click - Join Webinars', {});
        setShowWebinars(true);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (scrollLockRef.current) return;
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible) {
                    const key = (visible.target as HTMLElement).dataset.tab as TBillingTab;
                    if (key) setActiveTab(key);
                }
            },
            { rootMargin: '-150px 0px -55% 0px', threshold: 0 },
        );
        Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const toggleFaq = (idx: number) => {
        setOpenFaqs((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    const renderBenefits = () => (
        <div className={s.ibSection}>
            <h3 className={s.ibSectionTitle}>Explore the benefits of Managed billing</h3>
            <div className={s.ibBenefitsGrid}>
                <div className={`${s.ibBenefitCard} ${s.ibBenefitGreen}`}>
                    <div className={s.ibBenefitText}>
                        <div className={s.ibBenefitStatRow}>
                            <span className={s.ibBenefitStat}>3.9%</span>
                            <span className={s.ibBenefitInlineLabel}>industry-best rate</span>
                        </div>
                        <div className={s.ibBenefitSub}>Pay only when paid</div>
                    </div>
                    <img className={`${s.ibBenefitImg} ${s.ibImgCoins}`} src={benefitCoinsImg} alt="" />
                </div>
                <div className={`${s.ibBenefitCard} ${s.ibBenefitLavender}`}>
                    <div className={s.ibBenefitStatRow}>
                        <span className={s.ibBenefitStatMd}>94%</span>
                        <span className={s.ibBenefitInlineLabelMd}>first pass rate</span>
                    </div>
                    <div className={s.ibBenefitSub}>vs. 70–80% industry avg</div>
                </div>
                <div className={`${s.ibBenefitCard} ${s.ibBenefitPeach}`}>
                    <div className={s.ibBenefitStatMd}>&lt;24 hr</div>
                    <div className={`${s.ibBenefitInlineLabelMd} ${s.ibBenefitBlockLabel}`}>claim filed same-day</div>
                    <div className={s.ibBenefitSub}>after every session</div>
                </div>
                <div className={`${s.ibBenefitCard} ${s.ibBenefitGray}`}>
                    <div className={s.ibBenefitText}>
                        <div className={s.ibBenefitBlockStatMd}>0 denials left unworked</div>
                        <div className={s.ibBenefitSub}>every denial appealed</div>
                    </div>
                    <img className={`${s.ibBenefitImg} ${s.ibImgDoc}`} src={benefitDocImg} alt="" />
                </div>
            </div>
        </div>
    );

    const renderHow = () => (
        <div className={s.ibSection}>
            <h3 className={s.ibSectionTitle}>How Managed billing works</h3>
            <div className={s.ibHowitGrid}>
                {HOW_STEPS.map((item) => (
                    <div key={item.step} className={s.ibHowitCard}>
                        <div className={s.ibHowitIcon}>{item.icon}</div>
                        <div className={s.ibHowitStep}>{item.step}</div>
                        <div className={s.ibHowitTitle}>{item.title}</div>
                        <div className={s.ibHowitDesc}>{item.desc}</div>
                    </div>
                ))}
            </div>
            <p className={s.ibHowitEmail}>
                Questions about insurance billing? Email us at{' '}
                <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a>
            </p>
        </div>
    );

    const renderPricing = () => (
        <div className={s.ibSection}>
            <h3 className={s.ibSectionTitle}>Transparent pricing</h3>
            <div className={s.ibPricingWrap}>
                <div className={`${s.ibPricingCard} ${s.ibPricingHighlight}`}>
                    <div className={`${s.ibPricingLabel} ${s.ibPricingLabelKlarity}`}>Klarity</div>
                    <div className={`${s.ibPricingRate} ${s.ibPricingRateKlarity}`}>3.9%</div>
                    <div className={s.ibPricingNote}>of successful reimbursements. You only pay after you get paid.</div>
                </div>
                <div className={s.ibPricingCard}>
                    <div className={s.ibPricingLabel}>Legacy 3rd-party billers</div>
                    <div className={`${s.ibPricingRate} ${s.ibPricingRateLegacy}`}>5–10%</div>
                    <div className={s.ibPricingNote}>plus setup &amp; monthly fees.</div>
                </div>
            </div>
        </div>
    );

    const renderFaqs = () => (
        <div className={s.ibSection}>
            <h3 className={s.ibSectionTitle}>Frequently asked questions</h3>
            <div>
                {FAQS.map((item, idx) => (
                    <div key={item.q} className={cx(s.ibFaqItem, openFaqs[idx] && s.open)}>
                        <div className={s.ibFaqQ} onClick={() => toggleFaq(idx)}>
                            {item.q}
                            <svg
                                className={s.chev}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </div>
                        <div className={s.ibFaqA}>{item.a}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderFeedback = () => (
        <div className={s.ibSection}>
            <h3 className={s.ibSectionTitle}>Share feedback</h3>
            <p className={s.ibSectionSub}>
                Managed billing is in beta and we are shipping changes fast. Tell us what to fix or build next.
            </p>
            <button type="button" className={s.ibHeroCtaSecondary} onClick={() => {
                trackHeapEvent('WebApp - Insurance Billing Landing - Click - Share Feedback', {});
                window.open('mailto:providersupport@helloklarity.com', '_self');
            }}>
                Share feedback
            </button>
        </div>
    );

    const SECTIONS: { key: TBillingTab; render: () => React.ReactNode }[] = [
        { key: 'benefits', render: renderBenefits },
        { key: 'how', render: renderHow },
        { key: 'pricing', render: renderPricing },
        { key: 'faqs', render: renderFaqs },
        { key: 'feedback', render: renderFeedback },
    ];

    return (
        <div className={isTab ? s.wrapTab : s.wrap}>
            {!isTab && (
                <div className={s.topbar}>
                    <PPAOnboardingHeader showLogout webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
                </div>
            )}
            <div className={s.content}>
                {/* Hero */}
                <div className={s.ibHero}>
                    <div>
                        <h2 className={s.ibHeroTitle}>
                            Get paid faster with <span className={s.hl}>Managed billing</span>{' '}
                            <span className={s.ibBeta}>Beta</span>
                        </h2>
                        <p className={s.ibHeroSub}>
                            Automatically submit claims, fight denials, and track reimbursements after every session. No
                            biller needed. Works 24/7.
                        </p>
                        <div className={s.ibHeroActions}>
                            <button type="button" className={s.ibHeroCta} onClick={handleStart}>
                                Get started
                            </button>
                            <button type="button" className={s.ibHeroCtaSecondary} onClick={handleOpenWebinars}>
                                Sign-up for Webinars
                            </button>
                        </div>
                    </div>
                    <div className={s.ibHeroArt}>
                        <img src={heroImg} alt="Managed billing preview" className={s.ibHeroImg} />
                    </div>
                </div>

                {/* Tabs */}
                <div className={s.ibTabs}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={cx(s.ibTab, activeTab === tab.key && s.active)}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Sections */}
                {SECTIONS.map(({ key, render }) => (
                    <div
                        key={key}
                        data-tab={key}
                        ref={(el) => {
                            sectionRefs.current[key] = el;
                        }}
                        className={s.ibAnchor}
                    >
                        {render()}
                    </div>
                ))}
            </div>
            <NeedHelp
                show={showWebinars}
                onClose={() => setShowWebinars(false)}
                webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR}
            />
        </div>
    );
}
